import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getSnapshots } from '@/services/api';

export default function MetricDrillDown() {
  const { metricKey } = useParams<{ metricKey: string }>();
  const decodedKey = decodeURIComponent(metricKey ?? '');

  const { from, to } = useMemo(() => {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30).toISOString();
    const to = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
    return { from, to };
  }, []);

  const { data: snapshots, isLoading } = useQuery({
    queryKey: ['snapshots', from, to, decodedKey],
    queryFn: () => getSnapshots(from, to, decodedKey),
    enabled: !!decodedKey,
    staleTime: Infinity,
  });

  const sorted = [...(snapshots ?? [])].sort((a, b) => a.windowStart.localeCompare(b.windowStart));
  const maxVal = Math.max(...sorted.map(s => s.metricValue), 1);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/dashboard" className="text-brand-text-primary text-sm hover:underline">← Dashboard</Link>
        <h1 className="text-xl font-semibold">{decodedKey}</h1>
      </div>

      {isLoading && <p className="text-brand-text-secondary">Loading…</p>}

      {sorted.length === 0 && !isLoading && (
        <p className="text-brand-text-secondary">No data available for this metric in the last 30 days.</p>
      )}

      {sorted.length > 0 && (
        <div className="bg-white rounded shadow p-4">
          <div className="space-y-1">
            {sorted.map(s => (
              <div key={s.snapshotId} className="flex items-center gap-3 text-sm">
                <span className="w-24 text-brand-text-secondary text-xs">{new Date(s.windowStart).toLocaleDateString()}</span>
                <div
                  className="h-5 bg-brand-accent rounded"
                  style={{ width: `${Math.max((s.metricValue / maxVal) * 300, 4)}px` }}
                />
                <span className="text-brand-text-primary font-medium">{s.metricValue}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
